import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../i18n/translations';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'id';
  });
  
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  
  const [selectedBranch, setSelectedBranch] = useState(() => {
    return localStorage.getItem('selectedBranch') || 'all';
  });

  const [selectedRouter, setSelectedRouter] = useState(() => {
    return localStorage.getItem('selectedRouter') || 'all';
  });

  // Listen for resetBranch event from AuthContext
  useEffect(() => {
    const handleResetBranch = () => {
      setSelectedBranch('all');
    };
    
    window.addEventListener('resetBranch', handleResetBranch);
    return () => {
      window.removeEventListener('resetBranch', handleResetBranch);
    };
  }, []);

  // Auto-set branch for owner/kepala_cabang roles
  useEffect(() => {
    const handleSetUserBranch = (event) => {
      const { branch_id } = event.detail;
      if (branch_id) {
        setSelectedBranch(String(branch_id));
        localStorage.setItem('selectedBranch', String(branch_id));
      }
    };
    
    window.addEventListener('setUserBranch', handleSetUserBranch);
    return () => {
      window.removeEventListener('setUserBranch', handleSetUserBranch);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('theme', theme);
    // Note: 'dark' class on <html> is managed by useDashboardTheme hook
    // (based on the active dashboard theme's baseVariant). Adding/removing it
    // here would race with that hook. We only persist the legacy theme flag.
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('selectedBranch', selectedBranch);
  }, [selectedBranch]);

  useEffect(() => {
    localStorage.setItem('selectedRouter', selectedRouter);
  }, [selectedRouter]);

  // Reset router scope whenever branch changes (avoid stale router filter from another branch)
  useEffect(() => {
    setSelectedRouter('all');
    localStorage.setItem('selectedRouter', 'all');
  }, [selectedBranch]);

  // Reset branch to default 'all' - called on login/logout
  const resetBranch = () => {
    setSelectedBranch('all');
    localStorage.setItem('selectedBranch', 'all');
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'id' : 'en');
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    theme,
    setTheme,
    toggleTheme,
    selectedBranch,
    setSelectedBranch,
    selectedRouter,
    setSelectedRouter,
    resetBranch,
    t
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};